import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Search, Plus, ShoppingCart, ChevronLeft } from 'lucide-react';
import { storeApi, getCart, cartCount, minOrderQty, cartAdd, cartSetQty } from './storeApi';
import StoreLayout from './StoreLayout';
import QtyControl from './QtyControl';

// Map category name → emoji fallback for the circle when no product image exists.
const CATEGORY_FALLBACK_EMOJI = {
  'dairy': '🥛', 'bread': '🍞', 'eggs': '🥚',
  'fruits': '🍎', 'vegetables': '🥬', 'fruits & vegetables': '🥗',
  'cold drinks': '🥤', 'juices': '🧃', 'beverages': '🥤',
  'snacks': '🍿', 'munchies': '🍿',
  'breakfast': '🥣', 'instant food': '🍜',
  'sweet': '🍬', 'sweet tooth': '🍫', 'chocolate': '🍫',
  'bakery': '🥐', 'biscuits': '🍪',
  'tea': '🫖', 'coffee': '☕', 'milk drinks': '🥛',
  'atta': '🌾', 'rice': '🍚', 'dal': '🫘',
  'personal care': '🧴', 'pharmacy': '💊',
  'pet care': '🐾',
  'baby care': '🍼',
  'spices': '🌶️',
};

function emojiForCategory(name) {
  if (!name) return '🛒';
  const lower = name.toLowerCase();
  for (const [key, emoji] of Object.entries(CATEGORY_FALLBACK_EMOJI)) {
    if (lower.includes(key)) return emoji;
  }
  return '🛒';
}

// Image URLs in the DB may be:
//   • absolute http(s) URLs — used verbatim
//   • new `/api/files/...` (Emergent object storage) — same-origin, served via ingress
//   • `/api/uploads/...` (transitional local-disk era) — files were lost on each
//     deploy; rewrites to same path which 404s → we render the emoji fallback
//   • legacy `/uploads/...` — rewritten to `/api/uploads/...` for the ingress
function absImg(url) {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/uploads/')) return `/api${url}`;
  return url;
}

export default function StoreCatalogue() {
  const [data, setData] = useState({ by_category: [], total_products: 0, total_categories: 0 });
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [cart, setCart] = useState(getCart());
  const [activeCategory, setActiveCategory] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    setLoading(true);
    storeApi.get('/store/catalogue')
      .then((r) => setData(r.data))
      .catch(() => toast.error('Failed to load catalogue'))
      .finally(() => setLoading(false));
  }, []);

  // Stay in sync with header / other tabs after the cart store hydrates
  // from the server or mutates elsewhere.
  useEffect(() => {
    const onChange = (e) => setCart(e?.detail?.cart || getCart());
    window.addEventListener('storeCartChanged', onChange);
    return () => window.removeEventListener('storeCartChanged', onChange);
  }, []);

  // Filtered list based on (a) free-text search and (b) active category click.
  const visibleCategories = useMemo(() => {
    let base = data.by_category || [];
    if (activeCategory) base = base.filter((c) => c.category === activeCategory);
    if (q.trim()) {
      const needle = q.trim().toLowerCase();
      base = base
        .map((c) => ({
          ...c,
          products: c.products.filter(
            (p) =>
              p.name?.toLowerCase().includes(needle) ||
              p.sku?.toLowerCase().includes(needle) ||
              p.barcode?.toLowerCase().includes(needle) ||
              p.product_id?.toLowerCase().includes(needle),
          ),
        }))
        .filter((c) => c.products.length > 0);
    }
    return base;
  }, [q, data, activeCategory]);

  // KG products bypass min-qty enforcement per business rule (Feb 2026):
  // KG SKUs are sold by weight and the user should not see or be constrained
  // by a "MIN X KG" rule on the store portal. Backend order-allocation logic
  // is unaffected — this is purely a store-portal UI/UX simplification.
  const isKgProduct = (p) => (p?.unit || '').toUpperCase() === 'KG';
  const displayMinQty = (p) => (isKgProduct(p) ? 1 : minOrderQty(p));

  function changeQty(p, delta) {
    // Compute the new line qty locally so we can honour the "first add jumps
    // to min qty" rule, then push the absolute target to the server.
    const cur = Number(cart[p.id]?.qty || 0);
    const minQty = displayMinQty(p);
    let newQty;
    if (cur === 0 && delta > 0) {
      newQty = minQty; // first add — jump to minimum (1 for KG)
    } else {
      newQty = cur + delta;
      if (newQty < minQty) newQty = 0;
    }
    cartSetQty(p, newQty).catch((e) => toast.error(e?.message || 'Failed to update cart'));
  }

  // setQty — used by the QtyControl's direct input + quick-add chips. The
  // server is the source of truth; the cart state updates via the
  // storeCartChanged event listener once the server confirms.
  function setQty(p, target) {
    const minQty = displayMinQty(p);
    const desired = Number.isFinite(target) ? Math.floor(target) : 0;
    const final = (desired <= 0 || desired < minQty) ? 0 : desired;
    cartSetQty(p, final).catch((e) => toast.error(e?.message || 'Failed to update cart'));
  }

  function clickCategory(catName) {
    setActiveCategory(catName);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function clearCategory() {
    setActiveCategory(null);
  }

  const totalCartQty = cartCount(cart);
  const allCats = data.by_category || [];

  return (
    <StoreLayout>
      {/* Active-category breadcrumb */}
      {activeCategory && (
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={clearCategory}
            className="inline-flex items-center gap-1 text-sm font-medium text-orange-700 hover:text-orange-800"
            data-testid="back-to-all-categories"
          >
            <ChevronLeft className="w-4 h-4" />
            All categories
          </button>
          <h2 className="store-headline text-2xl text-orange-900">{activeCategory}</h2>
          <span />
        </div>
      )}

      {/* Search bar */}
      <div className="store-card p-4 mb-6 flex items-center gap-3">
        <Search className="w-5 h-5 text-orange-700 shrink-0" />
        <input
          className="flex-1 bg-transparent outline-none text-sm placeholder:text-slate-400"
          placeholder="Search by name, SKU, barcode…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          data-testid="store-catalogue-search"
        />
        <span className="text-xs text-slate-400 hidden sm:inline">
          {data.total_products} products · {data.total_categories} categories
        </span>
      </div>

      {/* Category circle strip — only on home view */}
      {!activeCategory && !loading && allCats.length > 0 && (
        <div className="mb-8" data-testid="category-strip">
          <h3 className="text-sm font-semibold text-slate-600 uppercase tracking-wider mb-3">
            Shop by Category
          </h3>
          <div className="flex gap-4 overflow-x-auto pb-3 -mx-1 px-1 scroll-smooth">
            {allCats.map((cat) => {
              const firstWithImg = cat.products.find((p) => p.image_url);
              return (
                <button
                  key={cat.category}
                  onClick={() => clickCategory(cat.category)}
                  className="shrink-0 flex flex-col items-center gap-2 w-24 group focus:outline-none"
                  data-testid={`category-tile-${cat.category}`}
                >
                  <div className="w-20 h-20 rounded-2xl bg-slate-50 border border-slate-200 group-hover:border-orange-400 flex items-center justify-center overflow-hidden transition-colors">
                    {firstWithImg ? (
                      <img
                        src={absImg(firstWithImg.image_url)}
                        alt={cat.category}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    ) : cat.image_url ? (
                      <img
                        src={absImg(cat.image_url)}
                        alt={cat.category}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.currentTarget.style.display = 'none'; }}
                      />
                    ) : (
                      <span className="text-3xl select-none">{emojiForCategory(cat.category)}</span>
                    )}
                  </div>
                  <p className="text-xs text-center font-medium text-slate-700 leading-tight line-clamp-2">
                    {cat.category}
                  </p>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Product grid */}
      {loading ? (
        <div className="text-center py-20 text-orange-700">Loading catalogue…</div>
      ) : visibleCategories.length === 0 ? (
        <div className="text-center py-20 text-slate-400">No products found.</div>
      ) : (
        <div className="space-y-8" data-testid="store-catalogue-list">
          {visibleCategories.map((cat) => (
            <section key={cat.category} className="space-y-3">
              {!activeCategory && (
                <div className="flex items-center justify-between border-b border-slate-200 pb-2">
                  <h2 className="store-headline text-2xl">{cat.category}</h2>
                  <button
                    onClick={() => clickCategory(cat.category)}
                    className="text-xs font-medium text-orange-700 hover:text-orange-800"
                    data-testid={`see-all-${cat.category}`}
                  >
                    See all ({cat.products.length})
                  </button>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {(activeCategory ? cat.products : cat.products.slice(0, 6)).map((p) => {
                  const line = cart[p.id];
                  const qty = line?.qty || 0;
                  // Stock-availability chip removed from Store Portal per
                  // business rule (Feb 2026). Backend order-allocation still
                  // enforces stock rules during Pending Procurement → Ready
                  // to Pick; store users never see "In Stock / Out of Stock".
                  const showMinChip = !isKgProduct(p) && minOrderQty(p) > 1;
                  return (
                    <div key={p.id} className="store-card p-4 flex flex-col gap-3" data-testid={`product-card-${p.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-semibold text-orange-950 truncate" title={p.name}>{p.name}</p>
                          <p className="text-xs text-slate-400 truncate">{p.product_id || p.sku || '—'}</p>
                        </div>
                      </div>
                      <div className="flex items-baseline gap-2">
                        <span className="text-xl font-bold text-slate-800">₹{Number(p.mrp).toFixed(2)}</span>
                        <span className="text-[10px] text-slate-400">MRP · / {p.unit}</span>
                        {showMinChip && (
                          <span className="ml-auto text-[10px] font-semibold text-orange-600 bg-orange-50 px-1.5 py-0.5 rounded">
                            MIN {minOrderQty(p)} {p.unit}
                          </span>
                        )}
                      </div>
                      <div className="flex items-end justify-end gap-2 mt-auto">
                        {qty === 0 ? (
                          <button onClick={() => changeQty(p, 1)} className="store-btn-citrus text-sm px-4 py-1.5"
                                  data-testid={`add-to-cart-${p.id}`}>
                            <Plus className="w-3 h-3 inline mr-1" /> Add
                          </button>
                        ) : (
                          <QtyControl
                            value={qty}
                            onChange={(newQty) => setQty(p, newQty)}
                            min={displayMinQty(p)}
                            quickAdds={[10, 25, 50, 100]}
                            compact
                            testidPrefix={`qty-${p.id}`}
                          />
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}

      {totalCartQty > 0 && (
        <button onClick={() => nav('/store/cart')} className="store-cart-fab" data-testid="cart-fab">
          <ShoppingCart className="w-5 h-5" />
          <span>{totalCartQty} item{totalCartQty > 1 ? 's' : ''} · Review cart</span>
        </button>
      )}
    </StoreLayout>
  );
}
